import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { combineLatest } from 'rxjs';

import { AppAuthService, ChainFacetsService, UserService } from './common';
import { AppMode, AppState } from './common/app-state';
import { Company, CompanySite, User } from './model';
import { TranslateModule } from '@ngx-translate/core';
import { ServerVersionService } from './server-version.service';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { filter } from 'rxjs/operators';
import { DocumentCanvasComponent } from './document-manager/document-canvas.component';
import { NgTemplateOutlet } from '@angular/common';

type FeatureType = 'risk' | 'eudr' | 'ppwr';

const FEATURE_PROPERTY: Record<
  FeatureType,
  keyof Pick<Company, 'enableRiskTool' | 'enableEudrTool' | 'enablePpwrTool'>
> = {
  risk: 'enableRiskTool',
  eudr: 'enableEudrTool',
  ppwr: 'enablePpwrTool',
};

@Component({
  selector: 'app-side-nav',
  imports: [
    MatExpansionModule,
    MatIconModule,
    MatListModule,
    MatDividerModule,
    MatSidenavModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    MatDialogModule,
    RouterModule,
    TranslateModule,
    NgTemplateOutlet,
  ],
  templateUrl: 'side-nav.component.html',
  styleUrls: ['side-nav.component.scss'],
})
export class SideNavComponent implements OnInit, AfterViewInit {
  protected moveFocusOutside(event: Event): void {
    const link = event.currentTarget as HTMLElement | null;
    setTimeout(() => {
      link?.blur();
      document.body.focus({ preventScroll: true });
    });
  }
  private readonly dialog = inject(MatDialog);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly appState = inject(AppState);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly serverVersionService = inject(ServerVersionService);
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly chainFacets = inject(ChainFacetsService);
  /**
   * The years of the selected site's chains, rendered as the sub-level of the chain list. Shared
   * with the list itself, so a count here always means the same thing as a count there.
   */
  readonly chainYears = this.chainFacets.years;
  readonly authService = inject(AppAuthService);
  selection?: { company: Company; site?: CompanySite };
  @ViewChild('sidenav') sidenav!: MatSidenav;
  companies: Company[] = [];
  mode: AppMode = AppMode.DEFAULT; // TODO this needs to go
  profile?: User;
  version?: string;
  private isDesktopLayout = false;
  private isLoggedIn = false;
  private syncQueued = false;
  isTracesDetailRoute = false;

  get isEvaluationRoute(): boolean {
    const path = this.router.url.split('?')[0];
    return /\/company\/[^/]+\/site\/[^/]+\/evaluation(\/|$)/.test(path);
  }
  
  isChainBuilderActive(kind: 'chain' | 'ppwr'): boolean {
    const tree = this.router.parseUrl(this.router.url);
    const path = tree.root.children['primary']?.segments.map((segment) => segment.path).join('/') ?? '';
    const suffix = kind === 'ppwr' ? '/ppwr/chain' : '/chain';
    if (!path.endsWith(suffix)) {
      return false;
    }
    return !Object.prototype.hasOwnProperty.call(tree.queryParams, 'year');
  }

  ngOnInit(): void {
    this.serverVersionService.subscribe(({ client }) => (this.version = client));
    this.appState.companies$.subscribe((companies) => (this.companies = companies));
    this.appState.companySite$.subscribe((companySite) => {
      this.selection = companySite;
      // No-op while the chain list is the active route — it has already resolved these facets.
      if (companySite?.site) {
        void this.chainFacets.ensure(companySite.site.id);
      }
    });
    this.appState.mode$.subscribe(async (mode) => {
      this.mode = mode;
      if (!this.sidenav) {
        return;
      }
      this.queueSidenavVisibilitySync();
    });
    this.authService.user$.subscribe(async (user) => {
      this.profile = user ? await this.userService.profile() : undefined;
    });
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.isTracesDetailRoute = this.isTracesDetailUrl(event.urlAfterRedirects);
      });
    this.isTracesDetailRoute = this.isTracesDetailUrl(this.router.url);
  }

  ngAfterViewInit() {
    this.appState.openSideNav$.subscribe(async (open) => {
      if (open && this.isLoggedIn) {
        await this.sidenav.open();
      } else {
        await this.sidenav.close();
      }
    });
    combineLatest([this.breakpointObserver.observe('(min-width: 1023px)'), this.authService.isLoggedIn$]).subscribe(
      async ([{ matches }, loggedIn]) => {
        this.isDesktopLayout = matches;
        this.isLoggedIn = loggedIn;
        this.queueSidenavVisibilitySync();
      }
    );
  }

  private queueSidenavVisibilitySync() {
    if (this.syncQueued) {
      return;
    }
    this.syncQueued = true;
    setTimeout(() => {
      this.syncQueued = false;
      void this.syncSidenavVisibility();
    });
  }

  private async syncSidenavVisibility() {
    this.sidenav.mode = this.isDesktopLayout ? 'side' : 'over';
    if (this.mode !== AppMode.EXTRA_WIDE && this.isDesktopLayout && this.isLoggedIn) {
      await this.sidenav.open();
      return;
    }
    await this.sidenav.close();
  }

  async switchBack() {
    await this.authService.switchBack();
    await this.router.navigate(['/']);
  }

  async toggle() {
    if (this.authService.isLoggedIn) {
      await this.sidenav.toggle();
    }
  }

  openCanvasManager() {
    const companyId = this.selection?.company?.id;
    const siteId = this.selection?.site?.id ?? this.selection?.company?.sites?.[0]?.id;
    if (!companyId || !siteId) {
      return;
    }
    this.dialog.open(DocumentCanvasComponent, {
      data: {
        storageKey: `document-canvas:${companyId}:${siteId}`,
      },
      width: '100vw',
      height: '100dvh',
      maxWidth: '100vw',
      maxHeight: '100dvh',
      panelClass: 'document-canvas-dialog',
      autoFocus: false,
      restoreFocus: false,
    });
  }

  get avatarInitials(): string {
    const source = this.profile?.name || this.profile?.email || 'SC';
    const matches = source?.match(/\b\w/g);
    return (matches?.length ? matches.slice(0, 2).join('') : source.substring(0, 2)).toUpperCase();
  }

  isFeatureDisabled = (type: FeatureType) => {
    const property = FEATURE_PROPERTY[type];
    return !this.selection?.company[property];
  };

  isFeatureHidden = (type: FeatureType) =>
    !!this.selection?.company.hideDisabledFeatures && this.isFeatureDisabled(type);

  get manageLink(): string[] | undefined {
    if (this.selection?.site) {
      return ['company', this.selection.company.id, 'site', this.selection.site.id, 'manage'];
    }
    if (this.companies?.length == 1) {
      return ['company', this.companies[0].id, 'site', this.companies[0].sites[0].id, 'manage'];
    }
    if (this.companies?.length == 0) {
      return ['company', 'create'];
    }
    return undefined;
  }

  get documentInboxLink(): string[] | undefined {
    const company = this.selection?.company ?? (this.companies?.length == 1 ? this.companies[0] : undefined);
    const site = this.selection?.site ?? company?.sites?.[0];
    return company?.id && site?.id ? ['/company', company.id, 'site', site.id, 'document-directory'] : undefined;
  }

  async logout() {
    await this.authService.logout();
    this.socialAuthService.signOut(true).finally(() => {
      setTimeout(() => (window.location.href = '/'));
      // TODO redirect to www.supplycanvas.com
    });
  }

  protected readonly AppMode = AppMode;

  private isTracesDetailUrl(url: string): boolean {
    const path = url.split('?')[0];
    return /\/traces-eudr(\/[^/]+)?$/.test(path) && !path.endsWith('/drafts') && !path.endsWith('/archive');
  }
}
